import { useMutation, useQueryClient } from '@tanstack/react-query';

import { addonUninstall } from '@api/sdk.gen';

import { withError, withInvalidate } from '@/react-tools/react-query';

import { openSwitchPrompt } from '@@/modals/SwitchPrompt';
import { ModalType } from '@@/modals';
import { buildConfirmButton } from '@@/modals/utils';

import { queryKeys } from '../query-keys';

export interface UninstallAddonOptions {
  /** Also discard the add-on's stored settings. Not reversible. */
  pruneConfig: boolean;
}

export function useUninstallAddon(id: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ pruneConfig }: UninstallAddonOptions) =>
      addonUninstall({ path: { id }, query: { pruneConfig } }),
    // Stay pending until the list refresh settles so callers redirect to an
    // up-to-date list. Invalidating list() also refreshes the switcher's lite
    // variant.
    ...withInvalidate(queryClient, [queryKeys.list(), queryKeys.detail(id)]),
    ...withError('Unable to uninstall add-on'),
  });
}

/**
 * Confirms an uninstall and asks whether to discard the add-on's stored settings.
 * Kept by default, matching the API: an add-on may have encrypted its data with a
 * stored key. Resolves undefined when cancelled.
 */
export async function confirmAddonUninstall(
  displayName: string
): Promise<UninstallAddonOptions | undefined> {
  const result = await openSwitchPrompt(
    `Uninstall ${displayName}`,
    'Prune add-on settings',
    {
      message: `This removes ${displayName}'s deployment. Its stored settings are kept, so reinstalling restores them — switch this on to discard them as well, which cannot be undone.`,
      confirmButton: buildConfirmButton('Uninstall', 'danger'),
      modalType: ModalType.Destructive,
      'data-cy': 'addon-uninstall-prune-config-switch',
    }
  );

  return result ? { pruneConfig: result.value } : undefined;
}
